import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { serverFetch } from "../../../server";
import { Button, message, Select } from "antd";
import { months } from "../../../extensions/helper";
import { fmtDate, getDateMonthFinish, getDateMonthStart, getDayOfTheWeek, strDate2Server } from "../../../extensions/utils";
import { LeftCircleOutlined, RightCircleOutlined } from "@ant-design/icons";
import "./calendar-days.css";
import CalendarMonth from "./calendar-month";
import CalendarDay from "./calendar-day";
import CalendarDow from "./calendar-dow";
import CalendarDayEdit from "./calendar-day-edit";

const dayjs = require('dayjs');
var isoWeek = require('dayjs/plugin/isoWeek');
dayjs.extend(isoWeek);

const CalendarDays = forwardRef(({ id }, ref) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState({ year: dayjs().year(), month: dayjs().month() + 1});
  const [copyItems, setCopyItems] = useState([]);
  const [edit, setEdit] = useState(false);
  const [selection, setSelection] = useState({ start: null, finish: null, mouseDown: false});

  // Ширина столбца месяцев
  const monthWidth = 40;
  // Ширина столбца дня
  const dayWidth = 100;
  // Высота шапки строки дней
  const headerHeight = 60;
  // Высота строки дней
  const dayHeight = 50;

  const getData = () => {
    if (id) {
      setLoading(true);
      const startDate = current.month === 1 ? getDateMonthStart(12, current.year - 1) : getDateMonthStart(current.month - 1, current.year);
      const finishDate= current.month === 12 ? getDateMonthFinish(1, current.year + 1) : getDateMonthFinish(current.month + 1, current.year);
      serverFetch(`calendar/CntList/${id}?start=${strDate2Server(startDate)}&finish=${strDate2Server(finishDate)}`)
        .then(data => {
          setLoading(false);
          setItems(data);
        })
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        })
      }
      else setItems([]);
  }

  // Предоставляем доступ к функции getData через ref
  useImperativeHandle(ref, () => ({
    getData
  }));

  useEffect(getData, [id, current]);

  // Начало выделения сектора
  const onMouseDown = date => setSelection({start: date, finish: date, mouseDown: true});
  // Конец выделения сектора
  const onMouseUp = date => setSelection({...selection, finish: date, mouseDown: false});
  // Движение мыши
  const onMouseMove = date => selection.mouseDown && setSelection({...selection, finish: date});
  // Выделена хотя бы одна ячейка
  const cellSelected = selection.start && selection.finish;
  // Определяем начало и окончание выделение в прямоугольнике
  let selectedStartDate = null;
  let selectedFinishDate = null;
  let selectedStartDow = 0;
  let selectedFinishDow = 0;
  if (cellSelected) {
    // Дни недели прямоугольника
    selectedStartDow = Math.min(getDayOfTheWeek(selection.start), getDayOfTheWeek(selection.finish));
    selectedFinishDow = Math.max(getDayOfTheWeek(selection.start), getDayOfTheWeek(selection.finish));
    selectedStartDate = selection.start.isBefore(selection.finish) 
      ? selection.start.startOf('isoWeek').add(selectedStartDow - 1,'day') 
      : selection.finish.startOf('isoWeek').add(selectedStartDow - 1,'day');
    selectedFinishDate = selection.start.isBefore(selection.finish) 
      ? selection.finish.startOf('isoWeek').add(selectedFinishDow - 1,'day') 
      : selection.start.startOf('isoWeek').add(selectedFinishDow - 1,'day');
  }
  //console.log(selectedStartDow,selectedFinishDow)
  // Проверка что указанная дата выделена
  const getDateIsSelected = (date) => {
    if (!cellSelected || !date) return false;
    // Чтобы дата была выделена она должна быть в промежутке между датами selectedStartDate и selectedFinishDate
    // и день недели также должен быть в промежутке между днями недели этих дат
    if (date.isBefore(selectedStartDate) || selectedFinishDate.isBefore(date)) return false;
    const res = getDayOfTheWeek(date) >= selectedStartDow && getDayOfTheWeek(date) <= selectedFinishDow;
    //console.log(date,res)
    return res;
  }

  const onCurrentChange = (value) => { setCurrent({...current, ...value}); }

  const yearOptions = Array.from({length: 7}, (_, i) => i + current.year - 3).map(x => ({label: `${x}`, value: x}))
  const disabled = loading || !id;

  // Колонки таблицы - первая - месяцы, остальные - дни недели
  const divCols = Array.from({ length: 8 }, (_, i) => i === 0 ? 
    [] : 
    [<CalendarDow dow={i} left={monthWidth + (i-1)*dayWidth} width={dayWidth} top={0} height={headerHeight} key={i}/>]);

  // Теперь перебираем все даты +- 1 месяц от выбранного
  let date = current.month === 1 ? getDateMonthStart(12, current.year - 1) : getDateMonthStart(current.month - 1, current.year);
  const finishDate = current.month === 12 ? getDateMonthFinish(1, current.year + 1) : getDateMonthFinish(current.month + 1, current.year);
  let stringIdx = 1;

  while (date < finishDate) {
    const dow = getDayOfTheWeek(date);
    // Новая неделя
    if (dow === 1) {
      stringIdx++;
    }
    divCols[dow].push(<CalendarDay 
      date={date} 
      left={monthWidth + dayWidth * (dow - 1)} 
      width={dayWidth} top={headerHeight + dayHeight * (stringIdx - 1)} height={dayHeight} 
      currentMonth={(date.month() + 1) === current.month}
      items={items.filter(i => fmtDate(i.date) === fmtDate(date) && i.start && i.finish)}
      changed={true && !items.find(i => (fmtDate(i.date) === fmtDate(date) && i.dayId !== 0))}
      selected={getDateIsSelected(date)}
      onMouseUp={onMouseUp}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      key={date}
      onCopy={(items) => setCopyItems(items)}
    />)

    // Новый месяц
    if (date.date() === 1) {
      let top = headerHeight + dayHeight * (stringIdx - 1);
      if (dow !== 1) top += dayHeight/2;
      // Определяем высоту контрола месяца - если месяц начинается с понедельника то всю высоту строки недели, иначе - половину
      let height = dow === 1 ? 0 : dayHeight/2;
      // Начальный день полной недели недели. Если начало месяца не понедельник то ищем ближайший
      let startDay = dow === 1 ? date : date.add(7 - dow + 1, 'day');
      // Анализируем дату окончания
      let finishDate = date.endOf('month');
      const fdow = getDayOfTheWeek(finishDate);
      // Если заканчивается не в воскресенье то ищем более ранее воскресенье
      let finishDay = fdow === 7 ? finishDate : finishDate.add(-fdow, 'day');
      if (fdow !== 7) height += dayHeight/2;
      height += Math.floor((finishDay.date() - startDay.date() + 1) / 7) * dayHeight;
      if (divCols[0].length === 0) {
        divCols[0].push(<CalendarMonth month={0} year={0} width={monthWidth} top={0} height={top} left={0} className="calendar-months" 
          currentMonth={(date.month() + 1) === current.month} key={-1}/>);
      }
      divCols[0].push(
        <CalendarMonth 
          month={date.month() + 1} 
          year={date.year()} 
          width={monthWidth} 
          top={top} 
          height={height} 
          left={0} 
          className="calendar-months" 
          currentMonth={(date.month() + 1) === current.month}
          key={date.month()}
        />);
    }
    date = date.add(1, 'day');
  }

  const onSetDayOff = () => {
    if (window.confirm("Сделать нерабочим?")) {
      setLoading(true);
      const dates = items.filter(i => getDateIsSelected(dayjs(i.date))).map(i => strDate2Server(i.date));
      serverFetch("calendar/cnt/SetDayOff", { method: "POST", bodyData: { calendarId: id, dates } })
        .then(getData)
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        })
    }
  }

  const onSetDefault = () => {
    if (window.confirm("Сделать график по умолчанию?")) {
      setLoading(true);
      const dates = items.filter(i => getDateIsSelected(dayjs(i.date))).map(i => strDate2Server(i.date));
      serverFetch("calendar/cnt/SetDefault", { method: "POST", bodyData: { calendarId: id, dates } })
        .then(getData)
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        })
    }
  }

  const onCloseDayEdit = (refresh) => {
    setEdit(false);
    refresh && getData();
  }

  // Скопировать все записи по текущей дате
  const onCopy = () => setCopyItems(items.filter(i => fmtDate(i.date) === fmtDate(selection.start) && i.start && i.finish))

  const onPaste = () => {
    if (window.confirm("Вставить графики в выделенные ячейки?")) {
      setLoading(true);
      const dates = items.filter(i => getDateIsSelected(dayjs(i.date))).map(i => strDate2Server(i.date));
      serverFetch("calendar/cnt/Paste", { method: "POST", bodyData: { calendarId: id, dates, source: copyItems } })
        .then(getData)
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        })
    }
  }

  return<div className="col2-col col2-col-last">
    <div className="sport-form-title">
      <Button disabled={disabled || !cellSelected} onClick={() => setEdit(true)}>Изменить</Button>
      <Button disabled={disabled || !cellSelected} onClick={onSetDayOff}>Не рабочий</Button>
      <Button disabled={disabled || !cellSelected} onClick={onSetDefault}>По умолчанию</Button>
      <Button disabled={disabled || !cellSelected || fmtDate(selection.start) !== fmtDate(selection.finish)} onClick={onCopy}>Копировать</Button>
      <Button disabled={disabled || !cellSelected || !copyItems} onClick={onPaste}>Вставить</Button>
      <div className="sport-form-title-group">
        <LeftCircleOutlined disabled={disabled} className="sport-btn-icon" onClick={() => onCurrentChange({year: current.year - 1})}/>
        <Select options={yearOptions} onChange={value => onCurrentChange({year: value})} value={current.year} disabled={disabled}/>
        <RightCircleOutlined disabled={disabled} className="sport-btn-icon" onClick={() => onCurrentChange({year: current.year + 1})}/>
      </div>
      <div className="sport-form-title-group">
        <LeftCircleOutlined disabled={disabled} className="sport-btn-icon" 
          onClick={() => onCurrentChange(current.month === 1 ? {month: 12, year: current.year - 1} : { month: current.month - 1})}/>
        <Select options={months} style={{width: 150}} onChange={value => onCurrentChange({month: value})} value={current.month} disabled={disabled}/>
        <RightCircleOutlined disabled={disabled} className="sport-btn-icon" 
          onClick={() => onCurrentChange(current.month === 12 ? {month: 1, year: current.year + 1} : { month: current.month + 1})}/>
      </div>
    </div>
    <div className="calendar-tbl">
        {divCols.map((item, idx) => idx === 0 ? 
          <div className="calendar-months" key={idx}>{item}</div> 
          : <div className="calendar-day" key={idx}>{item}</div>)}
    </div>
    {edit && <CalendarDayEdit 
      dates={[...new Set(items.filter(i => getDateIsSelected(dayjs(i.date))).map(i => strDate2Server(i.date)))]}
      items={items.filter(i => fmtDate(i.date) === fmtDate(selection.start) && i.start && i.finish)} 
      onClose={onCloseDayEdit} calendarId={id}
    />}
  </div>
});

export default CalendarDays;